import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonProperties } from '../../common/common.interfaces';
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../../common/common.entities';
import { SalsifyTableRow } from '../salsify.interfaces';
import { useLoading } from '../../loading/LoadingContext';
import { useUserContext } from '../../user/UserContext';
import { DateTime } from 'luxon';
import CustomError, { CustomErrorProps } from '../../common/components/CustomError';
import { apiFetch } from '../../utility/fetch';
import { API_PROD_URL } from '../..';
import RefreshSvg from '../../common/components/svg/RefreshSvg';
import EyeSvg from '../../common/components/svg/EyeSvg';
import { Badge, Button, Form } from 'react-bootstrap';
import XSvg from '../../common/components/svg/X-Svg';

interface SalsifyToProps extends CommonProperties {
  handleShowContent: any
}

export default function SalsifyTo(props: SalsifyToProps) {
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()

  const [salsifyRows, setSalsifyRows]: [SalsifyTableRow[], any]  = useState([])
  const [filteredRows, setFilteredRows]: [SalsifyTableRow[], any] = useState([])
  const [uniqueStatuses, setUniqueStatuses]: [string[], any] = useState([])

  const [selectedStatus, setSelectedStatus]: [string, any] = useState(undefined)
  const [searchMpn, setSearchMpn]: [string, any] = useState(undefined)
  
  const [refresh, setRefresh] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30)

  const totalPages = Math.ceil(filteredRows?.length > 0 ? filteredRows.length / pageSize : salsifyRows.length / pageSize);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows?.length > 0 ? filteredRows.slice(start, start + pageSize) : salsifyRows.slice(start, start + pageSize);
  }, [salsifyRows, currentPage, pageSize, filteredRows]);

  useEffect(() => {
    const load = async function(){
      changeStep('Salsify Sync')
      changeSubStep('Loading To Salsify records')
      toggleLoading(true)

      const response = await apiFetch<SalsifyTableRow[]>({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: API_PROD_URL,
        params: [
          {key: 'table', value: 'salsify'},
          {key: 'filter', value: `PartitionKey eq 'to-salsify'`}
        ]
      })

      const sortedRows = response.body.sort((a, b) => {
        return a.mpn.localeCompare(b.mpn)
      });

      const statuses: string[] = []
      for(const row of sortedRows){
        if(!statuses.includes(row.status)){
          statuses.push(row.status)
        }
      }

      setUniqueStatuses(statuses)

      setSalsifyRows(sortedRows)

      toggleLoading(false)
    }
    load()
  }, [refresh])

  const handleStatusChanged = useCallback((e) => {
    if(e.currentTarget.value === 'SELECT A STATUS') return
    setFilteredRows(salsifyRows.filter(r => r.status === e.currentTarget.value))
    setSelectedStatus(e.currentTarget.value)
  }, [salsifyRows])

  const handleSearchMpnChanged = useCallback((e) => {
    setFilteredRows(salsifyRows.filter(r => r.mpn.toLowerCase().includes(e.currentTarget.value.toLowerCase())))
    setSearchMpn(e.currentTarget.value)
  }, [salsifyRows])

  const handleClearFiltersClicked = useCallback((e) => {
    setSelectedStatus('SELECT A STATUS')
    setSearchMpn('')
    setFilteredRows([])
  }, [])

  const paginationControls = (
    <div className="mt-3 d-flex justify-content-center align-items-center gap-2 flex-wrap">
  
      <Button
        variant="secondary"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      >
        Previous
      </Button>
  
      {renderPageNumbers()}
  
      <Button
        variant="secondary"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
      >
        Next
      </Button>
    </div>
  );
  
  // Helper to render page buttons with ellipses
  function renderPageNumbers() {
    const pages: (number | 'ellipsis')[] = [];
    const visibleRange = 2;
  
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
  
      if (currentPage > visibleRange + 2) {
        pages.push('ellipsis');
      }
  
      const start = Math.max(2, currentPage - visibleRange);
      const end = Math.min(totalPages - 1, currentPage + visibleRange);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
  
      if (currentPage < totalPages - visibleRange - 1) {
        pages.push('ellipsis');
      }
  
      pages.push(totalPages);
    }
  
    return pages.map((item, idx) =>
      item === 'ellipsis' ? (
        <span key={`ellipsis-${idx}`} className="mx-1 beacon-label">...</span>
      ) : (
        <Button
          key={item}
          variant={item === currentPage ? 'success' : 'outline-secondary'}
          onClick={() => setCurrentPage(item)}
        >
          {item}
        </Button>
      )
    );
  }

  const view = useMemo(() => {
    if(!salsifyRows) return
    const {handleShowContent} = props



    return <>
    <div style={{paddingBottom: '80px'}}>
    <CustomError 
      closeError={() => {setError({visible: false})}}
      content={error?.content}
      description={error?.description}
      title={error?.title}
      visible={error?.visible}
    />
    <div>
      <span style={{cursor: 'pointer'}} title='Refresh the table.' onClick={() => {setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))}}>
        <RefreshSvg height={20} /></span><span className='beacon-label'> as of {refresh}
      </span>
      <span className='beacon-label'> with {salsifyRows.length} row(s) loaded and {filteredRows.length} row(s) filtered.</span>
    </div>
    <div>
      <table>
        <tbody>
          <tr>
            <td>
              <span className='beacon-label'>LOOKUP BY MPN</span>
              <Form.Control
                type='text'
                style={{width: '150px', height: '30px'}}
                placeholder='Enter an MPN...'
                onChange={handleSearchMpnChanged}
                value={searchMpn}
              />
            </td>
            <td>
              <span className='beacon-label'>LOOKUP BY STATUS</span>
              <Form.Select
                style={{width: '150px', height: '30px', }}
                onChange={handleStatusChanged}
                value={selectedStatus}
              >
                <option>SELECT A STATUS</option>
                {uniqueStatuses.map(us => (
                  <option key={us} value={us}>{us.toUpperCase()}</option>
                ))}
              </Form.Select>
            </td>
            <td>
              <br/>
              <span onClick={handleClearFiltersClicked} className='beacon-label' style={{cursor: 'pointer'}} title='Clear filters.'>
                <XSvg height={25} /> Clear Filters
              </span>
            </td>
          </tr>
        </tbody>
      </table>

    </div>
    <br/>
    <table className='flexport-table'>
      <thead>
        <tr className="sticky-row-2">
          <th></th>
          <th>MPN</th>
          <th>LINKS</th>
          <th>PAYLOAD</th>
          <th>UPDATED</th>
          <th>STATUS</th>
          <th>ERRORS</th>
        </tr>
      </thead>
      <tbody>
        {paginatedRows.map((row, index) => (
          <tr key={index}>
            <td><span style={{cursor: 'pointer'}} className='beacon-label'>{index + 1}</span></td>
            <td>
              <span className='beacon-label'>{row.mpn}</span>
            </td>
            <td>
              <span className='beacon-label'>
                <a className='beacon-link' title={'View the Product in Mission Control'} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${row.salesforceId}`}>SALESFORCE</a>
                &nbsp;<a className='beacon-link' title={`View the Product in Salsify`} target="_blank" rel="noopener noreferrer" href={`https://app.salsify.com/app/orgs/s-0c6fda80-f436-434d-889b-20868f61d979/products/v2/${row.mpn}`}>SALSIFY</a>
              </span>
            </td>
            {/* payload */}
            <td>
              <span 
                title={'View the payload sent to Salsify.'} 
                data-title={`To Salsify Payload Request for ${row.mpn}`} 
                data-content={row.payload}
                onClick={handleShowContent} 
                className='beacon-link'>
                  <EyeSvg height={25}/>
              </span>
            </td>
            <td><span>{DateTime.fromISO(row.timestamp).setZone('America/Chicago').toFormat('MM/dd/yyyy t')}</span></td>
            <td>
              <Badge bg={row.status === 'complete' ? 'success' : row.status === 'pending' ? 'warning' : 'danger'}>{row.status?.toUpperCase()}</Badge>
            </td>
            <td>
              <span className='beacon-label'>
                {row.errors}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  <div className='sticky-bottom'>
    {paginationControls}
  </div>
  </>
  }, [paginatedRows, searchMpn, selectedStatus])

  return view
}